import { Anchor, Box, Group, List, Paper, Stack, Table, Text } from '@mantine/core'

import { WIND_DOWN } from '@/config'
import { TextHeadingAlignmentFix } from '@/components'
import { useSelectedDenom } from '../../contexts/denom'

const targetDate = WIND_DOWN.targetDate.replace(', 2026', '')

const TIMELINE = [
  {
    date: 'Oct 12, 2026',
    status: 'Done',
    title: 'Liquid staking and new redemptions closed',
    description:
      'The v35 upgrade froze every redemption rate and began unbonding all staked tokens. stTokens stop earning staking rewards from here on.'
  },
  {
    date: `By ${WIND_DOWN.targetDate}`,
    status: 'Upcoming',
    title: 'Redeem on Osmosis',
    description:
      'Unbonded tokens move to Osmosis transmuter pools, where you can swap stTokens for native tokens at the fixed rate. Some pools may open earlier; we will link each one here as it goes live.'
  },
  {
    date: `By ${WIND_DOWN.targetDate}`,
    status: 'Upcoming',
    title: 'Stride stops producing blocks',
    description:
      'Before the chain halts, we will do our best to sweep balances off Stride to your matching addresses. The Osmosis pools stay open indefinitely after the halt.'
  }
]

const StakeWindDownCard: React.FC = () => {
  const [selectedDenom] = useSelectedDenom()

  return (
    <Paper p="xl" id="wind-down">
      <Stack spacing="lg">
        <Stack spacing="xs">
          <TextHeadingAlignmentFix
            adjustment="sm"
            sx={(t) => ({ fontSize: 20, fontWeight: 600, color: t.colors.gray[9] })}
          >
            Stride is winding down
          </TextHeadingAlignmentFix>

          <Text sx={(t) => ({ color: t.colors.gray[7] })}>
            Stride governance voted to wind down the Stride chain (
            <Anchor href={WIND_DOWN.proposalUrl} target="_blank">
              Proposal #286
            </Anchor>
            ,{' '}
            <Anchor href={WIND_DOWN.discussionUrl} target="_blank">
              full plan
            </Anchor>
            ). Liquid staking and redemptions on Stride are now closed.{' '}
            <strong>Your funds are not lost and there is no deadline to redeem.</strong>
          </Text>
        </Stack>

        <Stack spacing="sm">
          <Text weight={600} sx={(t) => ({ color: t.colors.gray[9] })}>
            When can I redeem my stTokens?
          </Text>

          <Text size="sm" sx={(t) => ({ color: t.colors.gray[7] })}>
            stTokens will be redeemable on Osmosis by {targetDate}, and some may be earlier. We&apos;ll link each pool
            here as it goes live.
          </Text>

          <Table fontSize="sm" verticalSpacing={6} horizontalSpacing="xs">
            <thead>
              <tr>
                <th>Token</th>
                <th style={{ textAlign: 'right' }}>Redeem on Osmosis</th>
              </tr>
            </thead>

            <tbody>
              {WIND_DOWN.redemptions.map((redemption) => {
                const isSelected = redemption.denom === selectedDenom

                return (
                  <Box
                    component="tr"
                    key={redemption.denom}
                    sx={(t) => ({ background: isSelected ? t.colors.pink[1] : undefined })}
                  >
                    <td>
                      <Text size="sm" weight={isSelected ? 700 : 600} sx={(t) => ({ color: t.colors.gray[9] })}>
                        st{redemption.denom}
                      </Text>
                    </td>

                    <td style={{ textAlign: 'right' }}>
                      {redemption.poolUrl ? (
                        <Anchor href={redemption.poolUrl} target="_blank" size="sm" weight={600}>
                          Live: swap now &rarr;
                        </Anchor>
                      ) : (
                        <Text size="sm" sx={(t) => ({ color: t.colors.gray[7] })}>
                          By {targetDate}
                        </Text>
                      )}
                    </td>
                  </Box>
                )
              })}
            </tbody>
          </Table>
        </Stack>

        <Stack spacing="sm">
          <Text weight={600} sx={(t) => ({ color: t.colors.gray[9] })}>
            Timeline
          </Text>

          {TIMELINE.map((step, i) => (
            <Box
              key={i}
              sx={(t) => ({
                borderLeft: `3px solid ${step.status === 'Done' ? t.colors.gray[4] : t.colors.pink[6]}`,
                paddingLeft: t.spacing.sm
              })}
            >
              <Group spacing="xs">
                <Text size="sm" weight={600} sx={(t) => ({ color: t.colors.gray[9] })}>
                  {step.date}
                </Text>

                <Text size="xs" sx={(t) => ({ color: t.colors.gray[6] })}>
                  {step.status}
                </Text>
              </Group>

              <Text size="sm" weight={600} sx={(t) => ({ color: t.colors.gray[8] })}>
                {step.title}
              </Text>

              <Text size="sm" sx={(t) => ({ color: t.colors.gray[7] })}>
                {step.description}
              </Text>
            </Box>
          ))}
        </Stack>

        <Stack spacing="sm">
          <Text weight={600} sx={(t) => ({ color: t.colors.gray[9] })}>
            What you should do
          </Text>

          <List size="sm" spacing="xs" sx={(t) => ({ color: t.colors.gray[7] })}>
            <List.Item>
              <strong>Holding stTokens or STRD on Stride?</strong> We&apos;ll do our best to sweep them to your matching
              Osmosis address before the chain halts. To be safe, you can move them to Osmosis yourself. Once your
              stToken&apos;s pool is live, swap it for the native token whenever you like.
            </List.Item>

            <List.Item>
              <strong>stTokens on another chain (e.g. Osmosis, Neutron)?</strong> Leave them there. There will be
              Osmosis pools for stTokens bridged from other chains, so no need to bridge back to Stride.
            </List.Item>

            <List.Item>
              <strong>Have a redemption in progress?</strong> It completes as normal and lands in your wallet on the
              native chain. You can track it below.
            </List.Item>

            <List.Item>
              <strong>Other tokens on Stride?</strong> We can&apos;t guarantee every balance is swept. Move anything you
              want to keep off Stride as soon as you can. Once the chain halts, tokens left on Stride will not be
              recoverable.
            </List.Item>

            <List.Item>
              <strong>Watch out for scams.</strong> Stride will never DM you or ask for your seed phrase. Redemption
              happens only through the Osmosis transmuter pools we link on this page.
            </List.Item>
          </List>
        </Stack>

        <Text size="sm" sx={(t) => ({ color: t.colors.gray[7] })}>
          To everyone who used Stride, secured the chain, or helped build the protocol: thank you.
        </Text>
      </Stack>
    </Paper>
  )
}

export { StakeWindDownCard }
